import { Deferred, pipe } from '@helo/utils';
import { addDefaultContext, applyTHISContext, containsRuntimeOperationConfig, executeRuntimeOperation, getContentNode, isKind, isRecord, isRuntimeOperationConfig, } from './runtime-processing.parts';
export { isKind } from './runtime-processing.parts';
export const defaultContext = {
    query: (queryId) => {
        return null;
    },
    queryVariables: (queryId) => {
        return null;
    },
    urlParams(key) {
        return null;
    },
    route: (routeId) => {
        return null;
    },
};
const setDefaultContext = (context) => {
    Object.assign(defaultContext, context);
    return defaultContext;
};
const getDefaultContext = () => {
    return defaultContext;
};
const createContextWithDefaults = (context) => {
    return Object.assign({}, defaultContext, context);
};
const processRuntimeOperationConfig = (value, context) => {
    if (!isRuntimeOperationConfig(value)) {
        return value;
    }
    return executeRuntimeOperation(Object.assign({}, value, {
        value: processRuntimeConfig(value.value, context),
    }), context);
};
const processRuntimeArray = (value, context) => {
    const processedValue = new Array(value.length);
    value.forEach((item, index) => {
        Deferred.setField(index, () => processRuntimeConfig(item, context))(processedValue);
    });
    return processedValue;
};
const processRuntimeRecord = (value, context) => {
    const processedValue = {};
    Object.entries(value).forEach(([key, item]) => {
        Deferred.addField(key, () => {
            if ('kind' in value && key === 'parent')
                return item;
            return processRuntimeConfig(item, context);
        })(processedValue);
    });
    return processedValue;
};
const processRuntimeState = (state, context) => {
    return processRuntimeRecord(state, context);
};
const shouldPassFieldStateToOfType = (contentNode, ofType) => {
    return (isKind('FIELD')(contentNode) &&
        !isKind('LIST')(ofType) &&
        !isKind('UNION')(ofType));
};
const createRuntimeChildNode = (contentNode, parentContentNode, context) => {
    return processRuntimeContentConfigNode({
        contentNode: Object.assign({}, contentNode, {
            parent: () => parentContentNode,
        }),
        context,
    });
};
const createRuntimeFieldOfType = (contentNode, originalOfType, context) => {
    const ofType = shouldPassFieldStateToOfType(contentNode, originalOfType)
        ? Object.assign({}, originalOfType, {
            state: pipe(contentNode.state ?? {}, Deferred.merge(originalOfType.state ?? {})),
        })
        : originalOfType;
    return createRuntimeChildNode(ofType, contentNode, context);
};
const defineLazyContentNodeChildren = (contentNode, context) => {
    switch (contentNode.kind) {
        case 'OBJECT':
        case 'INPUT_OBJECT': {
            const fields = contentNode.fields;
            Deferred.setField('fields', () => fields.map(field => createRuntimeChildNode(field, contentNode, context)))(contentNode);
            break;
        }
        case 'FIELD': {
            const fieldOfType = contentNode.ofType;
            Deferred.setField('ofType', () => createRuntimeFieldOfType(contentNode, fieldOfType, context))(contentNode);
            break;
        }
        case 'INPUT_VALUE': {
            const inputValueOfType = contentNode.ofType;
            Deferred.setField('ofType', () => createRuntimeChildNode(inputValueOfType, contentNode, context))(contentNode);
            break;
        }
        case 'LIST': {
            const items = contentNode.items;
            Deferred.setField('items', () => items?.map(item => createRuntimeChildNode(item, contentNode, context)) ?? [])(contentNode);
            break;
        }
        case 'ENUM': {
            const enumContentNode = contentNode.contentNode;
            Deferred.setField('contentNode', () => createRuntimeChildNode(enumContentNode, contentNode, context))(contentNode);
            break;
        }
        default:
            break;
    }
    return contentNode;
};
export const processRuntimeConfig = (value, context) => {
    if (!containsRuntimeOperationConfig(value)) {
        return value;
    }
    const contextWithDefaults = createContextWithDefaults(context);
    if (isRuntimeOperationConfig(value)) {
        return processRuntimeOperationConfig(value, contextWithDefaults);
    }
    if (Array.isArray(value)) {
        return processRuntimeArray(value, contextWithDefaults);
    }
    if (!isRecord(value)) {
        return value;
    }
    return processRuntimeRecord(value, contextWithDefaults);
};
const processState = () => (options) => {
    if (!containsRuntimeOperationConfig(options.contentNode.state)) {
        return options;
    }
    return Object.assign({}, options, {
        contentNode: Object.assign({}, options.contentNode, {
            state: processRuntimeState(options.contentNode.state, options.context),
        }),
    });
};
const processFields = () => (options) => {
    if (!isKind('OBJECT')(options.contentNode) &&
        !isKind('INPUT_OBJECT')(options.contentNode)) {
        return options;
    }
    return Object.assign({}, options, {
        contentNode: defineLazyContentNodeChildren(Object.assign({}, options.contentNode), options.context),
    });
};
const processOfType = () => (options) => {
    if (!isKind('FIELD')(options.contentNode) &&
        !isKind('INPUT_VALUE')(options.contentNode)) {
        return options;
    }
    return Object.assign({}, options, {
        contentNode: defineLazyContentNodeChildren(Object.assign({}, options.contentNode), options.context),
    });
};
const processListItems = () => (options) => {
    if (!isKind('LIST')(options.contentNode)) {
        return options;
    }
    return Object.assign({}, options, {
        contentNode: defineLazyContentNodeChildren(Object.assign({}, options.contentNode), options.context),
    });
};
const processEnumContentNode = () => (options) => {
    if (!isKind('ENUM')(options.contentNode)) {
        return options;
    }
    return Object.assign({}, options, {
        contentNode: defineLazyContentNodeChildren(Object.assign({}, options.contentNode), options.context),
    });
};
export const processRuntimeContentConfigNode = (options) => {
    if (!options.contentNode) {
        throw new Error('contentNode is required.');
    }
    return pipe(options, addDefaultContext(), applyTHISContext(), options => {
        switch (options.contentNode.kind) {
            case 'OBJECT':
            case 'INPUT_OBJECT':
                return pipe(options, processState(), processFields());
            case 'FIELD':
            case 'INPUT_VALUE':
                return pipe(options, processState(), processOfType());
            case 'LIST':
                return pipe(options, processState(), processListItems());
            case 'ENUM':
                return pipe(options, processState(), processEnumContentNode());
            case 'SCALAR':
                return pipe(options, processState());
            case 'ENUM_VALUE':
                return pipe(options, processState());
            default:
                return options;
        }
    }, getContentNode());
};
export const RuntimeOperations = {
    processRuntimeConfig,
    processRuntimeContentConfigNode,
    setDefaultContext,
    getDefaultContext,
};
//# sourceMappingURL=runtimeProcessing.js.map